import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { PageTitle } from '@/components/Page/Page';
import { RootState, useAppDispatch, useAppSelector } from '@/reduxStore/rootReducer';
import { openTransactionModal } from '@/reduxStore/slices/transactionsSlice';
import { StickyTable, Table } from '@monavate/ui-core';
import moment from 'moment';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { StatusTableBage } from '@monavate/ui-core';
import { get } from 'lodash';
import { OwnBankAccount } from '../../helpers/table';
import { findTransactionStatus } from '@/helpers/findTransactionStatus';
import { cardsApi, ibanApi, transactionsApi } from '../../services/rest/apiInstances';
import { AmountFormat } from '@monavate/ui-core';
import FiltersAdvanced from '../../components/FiltersAdvanced/FiltersAdvanced';
import { addNotification } from '../../reduxStore/slices/systemNotificationSlice';
import TransactionInfoRow from '@/components/TransactionInfoRow/TransactionInfoRow';
import { fetchOrdersList } from '../../reduxStore/slices/ordersSlice';
import LinkPaymentIcon from '@/components/LinkPaymentIcon/LinkPaymentIcon';
import { canLinkPayment } from '@/helpers/canLinkPayment';
import { Link } from '@monavate/ui-core';
import { PageContentState } from '@monavate/ui-core';
import { convertListToObjectsArray } from '../../helpers/convertListToObjectsArray';
import { getErrorMessageFromResponse } from '@/utils/errorHandling';
import LinkPaymentModal from '@/components/LinkPaymentModal/LinkPaymentModal';
import { useSelector } from 'react-redux';
import { buildClientApiConfig } from '@/helpers/apiRequestConfig';
import CustomPagination from '@/components/CustomPagination/CustomPagination';
import ScrollToSeeMore from '@/components/ScrollToSeeMore/ScrollToSeeMore';
import { FetchParams, usePrefetchPagination } from '@/hooks/usePrefetchPagination';
import { shouldShowPagination } from '@/utils/shouldShowPagination';
import {
    applyClientSideTransactionFilters,
    getSenderRecipientLabels,
} from '@/helpers/transactionFilters';
import {
    buildTransactionsApiFilters,
    transactionsFilterOptions,
    transactionsFilterValidationScheme,
    TransactionsFilters,
} from './transactionsFilters';

// Room left below the scroll area for the pagination bar and the page's bottom margin.
const PAGINATION_RESERVED_HEIGHT = 88;
// Per the ticket: 20 by default, then 50 and 100.
const PAGE_SIZE_OPTIONS = [20, 50, 100];

const ColumnCellContainer = styled.div`
    display: block;
    height: 50px;
    width: 100%;
    padding: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
`;

export const At = styled.div`
    color: ${(props) => props.theme['Content/secondary']};
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 22px;
`;

export const SecondaryItem = styled.div`
    font-size: 14px;
    color: ${(props) => props.theme['Content/secondary']};
`;

export const TableTag = styled.span`
    padding: 1px 8px;
    margin-right: 8px;
    background: ${(props) => props.theme['Status/neutral/bg']};
    color: ${(props) => props.theme['Content/primary']};
    font-family: Poppins;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 22px;
`;

const SenderLine = styled.div`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 22px;
`;

const RecipientLine = styled.div`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: ${(props) => props.theme['Content/secondary']};
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 22px;
`;

const TransactionsHeaderTable = styled.div`
    padding: 0 0 16px;
`;

const TableContainer = styled.div`
    position: relative;
`;

const checkIfHashPaymentInstruction = (location: any) => {
    const hash = location.hash;
    return hash.includes('paymentInstruction');
};

const renderSenderRecipient = (
    record: any,
    ownBankAccounts: OwnBankAccount[],
    clientName?: string,
) => {
    const { sender, recipient } = getSenderRecipientLabels(record, ownBankAccounts, clientName);
    return (
        <>
            <SenderLine>{sender}</SenderLine>
            <RecipientLine>{recipient}</RecipientLine>
        </>
    );
};

const TransactionsListPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const enabledCurrencies = useAppSelector((state: RootState) =>
        get(state, 'commonTerms.config.enabledCurrencies', []),
    );
    // Direction of a bankTransfer is derived from the client itself: settlement accounts come
    // from GET /ibans without an IBAN, so the client name is the only way to recognise them.
    const clientName = useAppSelector((state: RootState) =>
        get(state, 'commonTerms.config.name', ''),
    );
    const orderList = useAppSelector((state: RootState) => get(state, 'orders.orderList', []));
    const [availableCards, setAvailableCards] = useState<any[]>([]);
    const [ownBankAccounts, setOwnBankAccounts] = useState<OwnBankAccount[]>([]);
    const [filters, setFilters] = useState<TransactionsFilters | undefined>(undefined);
    const [totalTransactionsCount, setTotalTransactionsCount] = useState(0);
    const tableContainerRef = useRef<HTMLDivElement>(null);
    const filtersHeaderRef = useRef<HTMLDivElement>(null);
    const isOpenLinkPaymentModal = useSelector(
        (state: RootState) => state.linkPayment.isLinkModalOpen,
    );

    const apiFilters = useMemo(() => buildTransactionsApiFilters(filters), [filters]);

    // Kept for the notification below: a page load fires several requests in parallel, so the
    // reason is recorded here and reported once, rather than once per failed request.
    const lastFetchError = useRef<any>(null);

    const fetchTransactionsPage = useCallback(
        async ({ pageNumber, pageSize, ...requestFilters }: FetchParams) => {
            const options = buildClientApiConfig();
            try {
                const response = await transactionsApi.listTransactions(
                    { ...requestFilters, pageNumber, pageSize },
                    options,
                );
                setTotalTransactionsCount(response.data?.totalCount ?? 0);
                return { data: response.data?.transactions ?? [] };
            } catch (error: any) {
                lastFetchError.current = error;
                // Rethrown so the hook treats the page as failed and clears the rows, rather
                // than leaving the previous result on screen under the new filters.
                throw error;
            }
        },
        [],
    );

    // First load fetches the page plus the next two. After that, opening a page re-reads just
    // that page and tops up any window page it doesn't already hold.
    const {
        data: transactionList,
        loading: transactionsLoading,
        error: transactionsError,
        paginationProps,
        refresh,
    } = usePrefetchPagination<any>({
        fetchFn: fetchTransactionsPage,
        filters: apiFilters,
        config: {
            initialPageSize: PAGE_SIZE_OPTIONS[0],
            pageSizeOptions: PAGE_SIZE_OPTIONS,
            // Filters arrive from the blade on mount; fetching before then would fire an
            // unfiltered request that is immediately superseded.
            enabled: filters !== undefined,
        },
    });

    useEffect(() => {
        if (!transactionsError) {
            return;
        }
        dispatch(
            addNotification({
                message: getErrorMessageFromResponse(
                    lastFetchError.current,
                    'Failed to fetch transactions',
                ),
                status: 'error',
            }),
        );
    }, [transactionsError]);

    // Sender / Recipient and IBAN are not API parameters: they are applied here, to the rows
    // the API returned for the server-side filters — so they narrow the page that is on
    // screen rather than the whole result set.
    const visibleTransactions = useMemo(
        () =>
            applyClientSideTransactionFilters(
                transactionList,
                filters,
                ownBankAccounts,
                clientName,
            ),
        [transactionList, filters, ownBankAccounts, clientName],
    );

    useEffect(() => {
        const options = buildClientApiConfig();
        cardsApi
            .viewCards({}, options)
            .then((data) => {
                // @ts-ignore
                setAvailableCards(data.data.cards || []);
            })
            .catch((err) => {
                console.log(err);
            });
        ibanApi
            .listIBANAccounts(options)
            .then((data) => {
                // @ts-ignore
                setOwnBankAccounts(data.data.accounts || []);
            })
            .catch((err) => {
                console.log(err);
            });
        dispatch(fetchOrdersList({}));
    }, []);

    // The filter chips row appears and disappears above the table, so the height left for the
    // scroll area is measured rather than derived from the row count — a short filtered list
    // plus a chips row otherwise pushes the pagination bar below the fold.
    const [scrollAreaMaxHeight, setScrollAreaMaxHeight] = useState<string | undefined>(undefined);

    useLayoutEffect(() => {
        const container = tableContainerRef.current;
        if (!container) {
            return;
        }
        const update = () => {
            const documentTop = container.getBoundingClientRect().top + window.scrollY;
            setScrollAreaMaxHeight(
                `calc(100vh - ${Math.round(documentTop + PAGINATION_RESERVED_HEIGHT)}px)`,
            );
        };
        update();

        // Applied filters render as chips in the header, and enough of them wrap onto a second
        // row. Watching the header itself keeps the scroll area correct however tall it grows,
        // so the pagination bar never drops below the fold.
        const resizeObserver = new ResizeObserver(update);
        if (filtersHeaderRef.current) {
            resizeObserver.observe(filtersHeaderRef.current);
        }
        resizeObserver.observe(document.body);

        window.addEventListener('resize', update);
        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', update);
        };
        // The table only exists once loading finishes, so the measurement is re-attached then —
        // before that there is no container to observe.
    }, [transactionsLoading]);

    const columns = [
        {
            title: 'Transaction',
            dataIndex: 'id',
            key: 'id',
            ellipsis: true,
            width: '25%',
            render: (id: string, record: any) => (
                <ColumnCellContainer>
                    <TransactionInfoRow transaction={record} />
                </ColumnCellContainer>
            ),
        },
        {
            title: 'Date',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (createdAt: string) =>
                !!createdAt && (
                    <ColumnCellContainer>
                        {moment(createdAt).format('DD MMM YYYY')}
                        <At>{moment(createdAt).format('HH:mm:ss')}</At>
                    </ColumnCellContainer>
                ),
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            render: (amount: number, record: any) => (
                <ColumnCellContainer>
                    <AmountFormat amount={record.amount} currency={record.currency} letters />
                </ColumnCellContainer>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string, record: any) => {
                const currentStatus = findTransactionStatus(record);
                return (
                    <ColumnCellContainer>
                        <StatusTableBage status={currentStatus}>{currentStatus}</StatusTableBage>
                    </ColumnCellContainer>
                );
            },
        },
        {
            title: 'Order',
            dataIndex: 'orderReference',
            key: 'orderReference',
            render: (orderReference: string, record: any) => {
                const bankTransactionType = record?.bankTransaction?.bankTransactionType;
                const isNeedToDisplayEmptyCell =
                    bankTransactionType === 'settlement' ||
                    bankTransactionType === 'moneyOut' ||
                    bankTransactionType === 'moneyIn' ||
                    bankTransactionType === 'rebate';

                return isNeedToDisplayEmptyCell ? (
                    '-'
                ) : orderReference ? (
                    <ColumnCellContainer>
                        <Link
                            className="underline-hover"
                            handleClick={(event) => {
                                event?.stopPropagation();
                                navigate(`/orders/${orderReference}`);
                            }}
                        >
                            {orderReference}
                        </Link>
                    </ColumnCellContainer>
                ) : canLinkPayment(record) ? (
                    <ColumnCellContainer>
                        <LinkPaymentIcon transaction={record} />
                    </ColumnCellContainer>
                ) : (
                    '-'
                );
            },
        },
        {
            title: 'Supplier',
            dataIndex: 'supplierName',
            key: 'supplierName',
            render: (supplierName: string) => (
                <ColumnCellContainer>{supplierName ? supplierName : '-'}</ColumnCellContainer>
            ),
        },
        {
            title: 'Sender / Recipient',
            dataIndex: 'sender',
            key: 'sender',
            render: (sender: string, record: any) => (
                <ColumnCellContainer>
                    {renderSenderRecipient(record, ownBankAccounts, clientName)}
                </ColumnCellContainer>
            ),
        },
    ];

    useEffect(() => {
        const handlePopState = () => {
            if (checkIfHashPaymentInstruction(location) && transactionList.length > 0) {
                navigate(`/orders/${transactionList[0].orderReference}`);
            } else {
                navigate(-1);
            }
        };
        window.addEventListener('popstate', handlePopState);
        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [navigate, location.pathname, transactionList]);

    const returnCardLabelForFastFilter = (value: string) => {
        const card = availableCards.find((card) => card.id === value);
        if (card) {
            return <div>{card.name ? `${card.panLast4} ${card.name}` : card.panLast4}</div>;
        }
        return <div>{value}</div>;
    };

    // Linking a payment changes which transactions carry an order reference, so the list is
    // read again from the first page rather than served from the cache it has just invalidated.
    const afterLinkCallback = () => {
        refresh(1);
    };

    return (
        <div>
            <TransactionsHeaderTable ref={filtersHeaderRef}>
                <FiltersAdvanced
                    options={transactionsFilterOptions(
                        convertListToObjectsArray(enabledCurrencies),
                        availableCards,
                        orderList,
                        returnCardLabelForFastFilter,
                    )}
                    validationScheme={transactionsFilterValidationScheme}
                    bladeModalHeaderDescription={'Filter transactions'}
                    headersChildrenLeft={
                        <PageTitle data-test-id="page-title">Transactions</PageTitle>
                    }
                    onFilterUpdatesCallback={setFilters}
                />
            </TransactionsHeaderTable>
            <PageContentState
                empty={visibleTransactions.length === 0}
                loading={transactionsLoading}
            >
                <TableContainer>
                    <StickyTable
                        ref={tableContainerRef}
                        // A cap rather than a fixed height: a short list still sizes to its
                        // rows, a long one stops where the pagination bar begins. Row count
                        // can't decide this — a filter chips row changes what fits.
                        style={{ maxHeight: scrollAreaMaxHeight }}
                    >
                        <Table
                            dataSource={visibleTransactions}
                            columns={columns}
                            pagination={{
                                pageSize: visibleTransactions.length,
                                position: ['none' as 'bottomLeft'],
                            }}
                            onRow={(record: any) => {
                                return {
                                    onClick: () => {
                                        dispatch(
                                            openTransactionModal({
                                                ...record,
                                                transactionList: visibleTransactions,
                                            }),
                                        );
                                    },
                                };
                            }}
                            rowKey="id"
                            sticky={true}
                        />
                        <ScrollToSeeMore
                            containerRef={tableContainerRef}
                            watch={visibleTransactions}
                        />
                    </StickyTable>
                </TableContainer>
            </PageContentState>
            {shouldShowPagination(totalTransactionsCount, paginationProps) && (
                <CustomPagination {...paginationProps} />
            )}
            {isOpenLinkPaymentModal && (
                <LinkPaymentModal
                    isOpen={isOpenLinkPaymentModal}
                    afterLinkCallback={afterLinkCallback}
                />
            )}
        </div>
    );
};

export default TransactionsListPage;
